import type {
  EventStatus,
  PublishToEventItemFragment,
} from '@app/graphql/types-and-hooks';
import {
  AuthenticationState,
  TransactionFailedState,
  TransactionIdleState,
  TransactionProcessingState,
  TransactionState,
  TransactionSuccessState,
} from '@app/store/domain';

import { DATE_FILTER, EVENT_AREA, VIDEO_TYPE } from './routes/library/types';
import {
  ContentHistoryItem,
  GeneratedContentCache,
} from './routes/recordings/components/content-composer/domain';
import type {
  DraftEventPart,
  EventPart,
  VideoArea,
} from './routes/recordings/components/share-menu/types';
import {
  AdvancedEditsState,
  AiClipSuggestion,
  BrandingPreset,
  createDefaultAdvancedEditsState,
  DraftClip,
  EditorState,
  EditorTabs,
  TranscriptResponse,
} from './routes/recordings/domain';
import {
  ContentGenerationAttributesByType,
  ContentHistory,
  ContentRepurposingResponse,
  ContentType,
  EmailAudienceMember,
  GeneratedContent,
  ToneOfVoiceOption,
} from './routes/recordings/routes/lab/domain';
import {
  createDefaultContentHistory,
  createDefaultCreatorLabSelectedContent,
} from './routes/recordings/routes/lab/utils';

export type PageSummary = {
  externalId: string;
  title: string;
  slug: string;
  organizationExternalId: string;
  published: boolean;
  previewThumbnailPath: string | null;
};

type Pagination = {
  page: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
};

export type PageResponse = Pagination & {
  data: PageSummary[];
};

export type LibraryFilters = {
  searchText: string;
  videoType: VIDEO_TYPE;
  eventAreas: EVENT_AREA[];
  dateFilter: DATE_FILTER;
  events: {
    hasInitialised: boolean;
    selectedEvent?: {
      value: string;
      name: string;
    };
    options: EventFilterOption[];
    hasMoreOptions: boolean;
    endCursor: string | null;
    searchText?: string;
  } & TransactionState;
};

export type EventFilterOption = {
  id: string;
  name: string;
};

export type EventFilterResponse = {
  data: EventFilterOption[];
  pageInfo: {
    hasNextPage: boolean;
    endCursor?: string | null;
  };
};

export type EventInfo = {
  id: string;
  status?: EventStatus | null;
  timeEnd: string;
  expired: boolean;
  imageUrl?: string | null;
  name: string;
  slug: string;
  timeStart: string;
};

export type PublishToEventItem = PublishToEventItemFragment;

export type RecordingEventAssignment = {
  id: number;
  event_id: string;
  event_name: string;
  event_part_id: string | null;
  event_part_type: 'stage' | 'session' | null;
  event_part_name: string | null;
  published: boolean;
  created_at: string;
  updated_at: string;
};

export type EventSpeaker = {
  id: string;
  eventSpeakerId: string | null;
  firstName: string;
  lastName: string;
  headline: string;
  bio?: string | null;
  imageUrl?: string | null;
};

export type Config = {
  hasContentHubAccess?: boolean;
  hasContentHubCreatorLabAccess?: boolean;
};

export type PreRequisiteItemStatus = 'processing' | 'completed' | 'failed';

export type ContentRepurposingLoadingPhase =
  | {
      phase: 'initial' | 'failed' | 'ineligible';
    }
  | {
      phase: 'post-processing';
      processingDetails: {
        topics: PreRequisiteItemStatus;
        toneOfVoice: PreRequisiteItemStatus;
      };
    }
  | {
      phase: 'completed';
    };

export type SpeakerWithOptionalId = EventSpeaker | Omit<EventSpeaker, 'id'>;

export type SpeakersListV2Response = {
  cursor: string | null;
  data: SpeakerWithOptionalId[];
};

export type SpeakersListState = {
  data: SpeakerWithOptionalId[];
  pagination: {
    orgSpeakers: {
      next_cursor: string | null;
    };
    eventSpeakers: {
      endCursor: string | null;
      hasNextPage: boolean;
    };
  };
  searchText: string;
} & TransactionState;

export type State = {
  authentication: AuthenticationState;
  config: Config & TransactionState;
  pageInfo: {
    searchText: string;
    currentPage: number;
    isPagesLoaded: boolean;
    pages:
      | ({ data: null; pagination: null } & (
          | TransactionIdleState
          | TransactionProcessingState
          | TransactionFailedState<Record<string, string>>
        ))
      | ({
          data: Record<number, PageSummary[]>;
          pagination: {
            page: number;
            totalPages: number;
            totalRecords: number;
            pageSize: number;
          };
        } & TransactionSuccessState);
    pageDeletionInfo: {
      isDialogOpen: boolean;
      pageExternalId?: string;
      pageTitle?: string;
    } & TransactionState;
    pageDuplication?: {
      pageExternalId?: string;
    } & TransactionState;
  };
  libraryInfo: {
    filters: LibraryFilters;
    isLibraryLoaded: boolean;
    recordings: RecordingList & TransactionState;
    recordingDeletionInfo: {
      isDialogOpen: boolean;
      recordingId?: string;
      recordingTitle?: string;
      hasSimulive: boolean;
      hasUpcomingStudios: boolean;
      eventPartType?: EventArea | null;
    } & TransactionState;
    recordingRenameInfo: {
      isDialogOpen: boolean;
      recordingId?: string;
      recordingTitle?: string;
      newTitle?: string;
    } & TransactionState;
    recentContent: {
      data: RecentContent;
    } & TransactionState;
  };
  recordingInfo: {
    recordingRedirectFailed: boolean;
    data: Recording | null;
    backUrl?: string;
    playback: RecordingPlaybackState;
    error?: {
      message: string;
    };
    eventInfo: EventInfo | null;
    eventSpeakers: SpeakersListState;
    editorState: EditorState;
    clipDeletionInfo: {
      isDialogOpen: boolean;
      clipId?: string;
      clipTitle?: string;
      clipType?: string;
    } & TransactionState;
    descriptionUpdateState: TransactionState;
    titleEditState: TransactionState;
    clipExportInfo: {
      isDialogOpen: boolean;
      clipsToExport: DraftClip[];
    } & TransactionState;
    clipUpdateInfo: TransactionState;
    clipDiscardAllInfo: {
      isDialogOpen: boolean;
    } & TransactionState;
    publishToEvents: {
      searchText: string;
      eventsList: PublishToEventItem[];
      pageInfo: {
        hasNextPage: boolean;
        endCursor: string | null;
      };
      videoAreas: {
        byEventId: Record<
          string,
          {
            data: VideoArea[];
          } & TransactionState
        >;
      };
      initialSelectedEvents: Record<string, EventPart>;
      draftSelectedEvents: Record<string, DraftEventPart>;
      eventAreaValidationErrors: Record<string, boolean>;
      assignmentsStatus: 'idle' | 'processing' | 'success' | 'failed';
    } & TransactionState;
    transcript: {
      data: TranscriptResponse | null;
    } & TransactionState;
    transcriptSearchQuery: string;
    selectedContextMenuTabId: EditorTabs | null;
    speakerManagement: {
      editingState:
        | 'closed'
        | 'select-speaker'
        | 'create-speaker'
        | 'edit-speaker';
      speakerKey?: string;
      currentSpeakerName?: string;
      speaker?: EventSpeaker;
      isUploading?: boolean;
      uploadedImageUrl?: string;
    } & TransactionState;
    colorPresets: {
      presets: BrandingPreset[];
    } & TransactionState;
    captions: {
      enabled: boolean;
    };
    advancedEdits: AdvancedEditsState;
    /**
     * AI clip suggestions for the current recording.
     * Completely separate from editorState.draft_clips / legacy clips.
     * Once triggered, the suggest button is blocked (POST returns same response).
     */
    aiClipSuggestion: {
      data: AiClipSuggestion | null;
      triggerStatus: 'idle' | 'loading' | 'failed';
      fetchStatus: 'idle' | 'loading' | 'success' | 'failed';
      /**
       * Tracks which clip indices are mid-convert.
       * Clips have no id so we use their array index as the key.
       */
      convertingClipIndices: Record<number, boolean>;
      /**
       * Set to the new edit's id when an AI clip convert succeeds.
       * The editor-v2 component watches this and navigates to the edit view.
       * Null at all other times.
       */
      lastAiConvertedEditId: string | null;
    };
  } & TransactionState;
  creatorLab: {
    contentRepurposing: {
      loadingPhase: ContentRepurposingLoadingPhase;
      data: ContentRepurposingResponse | null;
    } & TransactionState;
    toneOfVoiceConfig: {
      data: ToneOfVoiceOption[] | null;
    } & TransactionState;
    emailAudienceConfig: {
      data: EmailAudienceMember[] | null;
    } & TransactionState;
    contentHistory: {
      [K in ContentType]: ContentHistory;
    };
    generatedContent: Record<
      string,
      GeneratedContent & TransactionState
    > | null;
    selectedContent: CreatorLabSelectedContent | null;
    isContentGenerationRequestInProgress: boolean;
    isContentLoading: boolean;
  };
  contentComposer: {
    isInitialized: boolean;
    contentRepurposing: {
      data: ContentRepurposingResponse | null;
    } & TransactionState;
    toneOfVoiceConfig: {
      data: ToneOfVoiceOption[] | null;
    } & TransactionState;
    emailAudienceConfig: {
      data: EmailAudienceMember[] | null;
    } & TransactionState;
    contentHistory: {
      [K in ContentType]: ContentComposerContentHistory;
    };
    feedback: {
      [K in ContentType]: {
        [contentId: string]: {
          options: {
            [optionIndex: number]: 'thumbs_up' | 'thumbs_down' | null;
          };
        };
      };
    };
    generationAttributes: ContentComposerGenerationAttributes;
    /** Cache of fetched generated content, keyed by content ID */
    generatedContent: GeneratedContentCache;
    isContentGenerationRequestInProgress: boolean;
  };
  orgLevelRecordingUpload: {
    isUploading: boolean;
    progress: number;
    completed: boolean;
  };
};

export type CreatorLabSelectedContentByType = {
  [K in ContentType]: {
    id: string | null;
    type: K;
    attributes: ContentGenerationAttributesByType[K];
  };
};

export type CreatorLabSelectedContent =
  CreatorLabSelectedContentByType[ContentType];

/**
 * Content Composer generation attributes
 * Single shared object tracking selected tone, topic, email audience, and email type
 * All fields are optional and shared across content types
 */
export type ContentComposerGenerationAttributes = {
  toneOfVoiceId: string | null;
  topicId: string | null;
  emailAudienceId: string | null;
  emailTypeId: string | null;
};

/**
 * Content history state for Content Composer with metadata items and total count
 */
export type ContentComposerContentHistory = {
  history: ContentHistoryItem[];
  cursor: string | null;
  total: number;
} & TransactionState;

/**
 * Creates default generation attributes
 */
export function createDefaultContentComposerGenerationAttributes(): ContentComposerGenerationAttributes {
  return {
    toneOfVoiceId: null,
    topicId: null,
    emailAudienceId: null,
    emailTypeId: null,
  };
}

/**
 * Creates default content history for Content Composer with enriched items and total
 */
export function createDefaultContentComposerContentHistory(): {
  [K in ContentType]: ContentComposerContentHistory;
} {
  return {
    SOCIAL_POST: {
      history: [],
      cursor: null,
      total: 0,
      status: 'idle',
    },
    BLOG_POST: {
      history: [],
      cursor: null,
      total: 0,
      status: 'idle',
    },
    EMAIL: {
      history: [],
      cursor: null,
      total: 0,
      status: 'idle',
    },
  };
}

/**
 * Creates the default state for Content Composer slice
 */
export function createDefaultContentComposerState() {
  return {
    isInitialized: false,
    contentRepurposing: {
      data: null,
      status: 'idle' as const,
    },
    toneOfVoiceConfig: {
      data: null,
      status: 'idle' as const,
    },
    emailAudienceConfig: {
      data: null,
      status: 'idle' as const,
    },
    contentHistory: createDefaultContentComposerContentHistory(),
    feedback: {
      SOCIAL_POST: {},
      BLOG_POST: {},
      EMAIL: {},
    },
    generationAttributes: createDefaultContentComposerGenerationAttributes(),
    /** Cache of fetched generated content, keyed by content ID */
    generatedContent: {} as GeneratedContentCache,
    isContentGenerationRequestInProgress: false,
  };
}

export type { ContentType };

export const defaultState: State = {
  authentication: {
    status: 'idle',
    contentApiAuthToken: null,
    contentApiEndpoint: null,
  },
  config: {
    status: 'idle',
    hasContentHubAccess: undefined,
    hasContentHubCreatorLabAccess: undefined,
  },
  pageInfo: {
    searchText: '',
    currentPage: 1,
    isPagesLoaded: false,
    pages: {
      status: 'idle',
      data: null,
      pagination: null,
    },
    pageDeletionInfo: {
      isDialogOpen: false,
      status: 'idle',
    },
    pageDuplication: {
      status: 'idle',
    },
  },
  libraryInfo: {
    filters: {
      searchText: '',
      videoType: 'all',
      eventAreas: [],
      dateFilter: 'all',
      events: {
        hasInitialised: false,
        selectedEvent: undefined,
        options: [],
        hasMoreOptions: false,
        endCursor: null,
        status: 'idle',
      },
    },
    isLibraryLoaded: false,
    recordings: {
      data: [],
      pagination: {
        next_cursor: null,
      },
      status: 'idle',
    },
    recordingDeletionInfo: {
      isDialogOpen: false,
      status: 'idle',
      hasSimulive: false,
      hasUpcomingStudios: false,
      eventPartType: null,
    },
    recordingRenameInfo: {
      isDialogOpen: false,
      status: 'idle',
    },
    recentContent: {
      data: {
        SOCIAL_POST: null,
        BLOG_POST: null,
        EMAIL: null,
      },
      status: 'idle',
    },
  },
  recordingInfo: {
    recordingRedirectFailed: false,
    data: null,
    playback: {
      currentTime: 0,
      duration: 0,
      isPlaying: false,
      isPortraitFullscreen: false,
    },
    status: 'idle',
    eventInfo: null,
    eventSpeakers: {
      data: [],
      pagination: {
        orgSpeakers: {
          next_cursor: null,
        },
        eventSpeakers: {
          endCursor: null,
          hasNextPage: false,
        },
      },
      searchText: '',
      status: 'idle',
    },
    editorState: {
      clip_suggestion: null,
      draft_clips: [],
      selectedClip: null,
    },
    clipDeletionInfo: {
      isDialogOpen: false,
      status: 'idle',
    },
    clipExportInfo: {
      isDialogOpen: false,
      clipsToExport: [],
      status: 'idle',
    },
    clipDiscardAllInfo: {
      isDialogOpen: false,
      status: 'idle',
    },
    publishToEvents: {
      searchText: '',
      eventsList: [],
      pageInfo: { hasNextPage: false, endCursor: null },
      videoAreas: {
        byEventId: {},
      },
      initialSelectedEvents: {},
      draftSelectedEvents: {},
      eventAreaValidationErrors: {},
      assignmentsStatus: 'idle',
      status: 'idle',
    },
    descriptionUpdateState: {
      status: 'idle',
    },
    titleEditState: {
      status: 'idle',
    },
    clipUpdateInfo: {
      status: 'idle',
    },
    transcript: {
      data: null,
      status: 'idle',
    },
    transcriptSearchQuery: '',
    selectedContextMenuTabId: null,
    speakerManagement: {
      editingState: 'closed',
      status: 'idle',
    },
    colorPresets: {
      presets: [],
      status: 'idle',
    },
    captions: {
      enabled: true,
    },
    advancedEdits: createDefaultAdvancedEditsState(),
    aiClipSuggestion: {
      data: null,
      triggerStatus: 'idle',
      fetchStatus: 'idle',
      convertingClipIndices: {},
      lastAiConvertedEditId: null,
    },
  },
  creatorLab: {
    contentRepurposing: {
      data: null,
      loadingPhase: { phase: 'initial' },
      status: 'idle',
    },
    toneOfVoiceConfig: {
      data: null,
      status: 'idle',
    },
    emailAudienceConfig: {
      data: null,
      status: 'idle',
    },
    contentHistory: createDefaultContentHistory(),
    generatedContent: null,
    selectedContent: createDefaultCreatorLabSelectedContent('SOCIAL_POST'),
    isContentGenerationRequestInProgress: false,
    isContentLoading: true,
  },
  contentComposer: createDefaultContentComposerState(),
  orgLevelRecordingUpload: {
    isUploading: false,
    progress: 0,
    completed: false,
  },
};

export type RecordingCategory =
  | 'main'
  | 'backup'
  | 'rehearsal'
  | 'upload'
  | 'default';

export type RecordingPlaybackState = {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  isPortraitFullscreen: boolean;
  muted?: boolean;
  /** When true, video player context will seek to selected clip start and play once the active element is ready */
  pendingSeekAndPlay?: boolean;
};

export type EventArea = 'stage' | 'session';

export type VideoStaus =
  | 'success'
  | 'failure'
  | 'processing'
  | 'trimming_requested';

export type TranscriptionStatus =
  | 'unknown'
  | 'no_audio'
  | 'processing'
  | 'ready'
  | 'failed';

// API response for single Recording
export type Recording = {
  id: string;
  event_id: string | null;
  title: string;
  description: string;
  category: RecordingCategory;
  duration: number;
  created_at: string;
  started_at: string;
  thumbnail: string | null;
  ended_at: string | null;
  playable_url: string | null;
  download_url: string | null;
  event_part_name: string;
  event_part_type: EventArea;
  event_part_id: string;
  event_name: string;
  published: boolean;
  parent_recording_id: string | null;
  simulives: Simulive[];
  upcoming_studios?: UpcomingStudio[];
  parent: Recording | null;
  status: VideoStaus;
  status_changed_at: string | null;
  is_original: boolean;
  transcription_status: TranscriptionStatus;
  related: Recording[];
  aspect_ratio?: '9_16' | '16_9' | null;
  creator_id?: string | null;
  creator_name?: string | null;
};

export type PageCursor = {
  next_cursor: string | null;
};

export type RecordingListResponse = {
  data: Recording[];
  pagination: PageCursor;
};

export type RecordingList = {
  data: Recording[];
  pagination: PageCursor;
};

export type MediaItem = {
  id: string;
  path: string;
};

export type MediaItemWithUploadUrl = MediaItem & {
  uploadUrl: string;
};

export type RecentContentItem = {
  recordingId: string;
  externalId: string;
  text: string;
};

export type Simulive = {
  id: number;
  uuid: string;
  video_channel_id: number;
  schedule_id: number;
};

export type UpcomingStudio = {
  id: number;
  name: string;
  event_id: number;
  event_name: string;
};

export type RecentContent = Record<ContentType, RecentContentItem | null>;
